import {
  useWSModalCustom,
  useWSSnackbar,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect
} from "@wingspanhq/fe-component-library";
import { WorkDefinition } from "@wingspanhq/wingspan-api-sdk";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { queriesWorkItems } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { FormPartialAttributes } from "../../components/FormPartialAttributes";
import { AcceptedValuesResources } from "../../components/FormPartialAttributes/helpers";
import { getAttributeValidators } from "../../components/FormPartialAttributes/validation";
import { stripNullAttributes } from "../../utils/stripNullAttributes";

type FormType = {
  workDefinitionId: string;
  [key: string]: any;
};

type Props = {
  workLogId: string;
  payerId: string;
  engagementId: string;
  workDefinitions: WorkDefinition[];
  resources: AcceptedValuesResources;
} & WSModalCustomProps<any>;

export const ModalAddItem: React.FC<Props> = ({
  workLogId,
  payerId,
  engagementId,
  workDefinitions,
  resources,
  ...modalProps
}) => {
  const workDefinitionOptions = useMemo(
    () =>
      workDefinitions.map(workDefinition => ({
        value: workDefinition.id ?? "",
        label: workDefinition.name ?? ""
      })),
    [workDefinitions]
  );

  const [mutate, meta] = queriesWorkItems.createWorkItem.useMutation();

  const defaultWorkDefinitionId =
    workDefinitions.length === 1 ? workDefinitions[0].id : undefined;
  const [workDefinitionId, setWorkDefinitionId] = useState<string | undefined>(
    defaultWorkDefinitionId
  );

  const workDefinition = useMemo(() => {
    if (!workDefinitionId) {
      return;
    }

    return workDefinitions.find(
      definition => definition.id === workDefinitionId
    );
  }, [workDefinitions, workDefinitionId]);

  const validationSchema = useMemo(() => {
    if (!workDefinition) {
      return;
    }

    const attributeValidators = getAttributeValidators(
      workDefinition.attributeDefinitions ?? []
    );

    return Yup.object().shape({
      workDefinitionId: Yup.string().required("Required"),
      ...attributeValidators
    });
  }, [workDefinition]);

  const { openSnackbar } = useWSSnackbar();

  return (
    <WSForm<FormType>
      validationSchema={validationSchema}
      defaultValues={{
        workDefinitionId,
        defaultWorkDefinitionId
      }}
      onSubmit={({ workDefinitionId, ...attributes }) => {
        mutate(
          {
            workItemCreate: {
              workLogId,
              workDefinitionId,
              attributes: stripNullAttributes(attributes)
            }
          },
          {
            onSuccess: () => {
              modalProps.onClose(undefined);
              openSnackbar({
                icon: {
                  name: "check"
                },
                message: "Work item added"
              });
            }
          }
        );
      }}
    >
      <WSModal
        title="Add work"
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              type: "button",
              onClick: () => {
                modalProps.onClose(undefined);
              }
            },
            {
              kind: "Primary",
              label: "Add work",
              loading: meta.isLoading
            }
          ]
        }}
        {...modalProps}
      >
        <WSList gap="L">
          <WSForm.Field
            name="workDefinitionId"
            label="Work item"
            component={WSSelect}
            onChange={newValue => {
              setWorkDefinitionId(newValue);
            }}
            componentProps={{
              required: true,
              options: workDefinitionOptions
            }}
          />

          <WSForm.Value name="workDefinitionId">
            {workDefinitionId => {
              const workDefinition = workDefinitions.find(
                definition => definition.id === workDefinitionId
              );

              if (!workDefinition) {
                return null;
              }

              return (
                <FormPartialAttributes
                  attributeDefinitions={
                    workDefinition.attributeDefinitions ?? []
                  }
                  resources={resources}
                />
              );
            }}
          </WSForm.Value>

          <ErrorAlert error={meta.error} context="WorkItem" />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalAddItem = () => useWSModalCustom(ModalAddItem);
